import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  Input,
  OnChanges,
  SimpleChanges,
  OnInit
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';
import { AppConfig } from '@app/core/app.config';
import { EntityTypeEnum } from '@app/core/enums/entity-type.enum';
import { PermissionEnum } from '@app/core/enums/permissions.enum';
import { ActionTypeEnum, ManualWorkflowViewEnum, Workflow } from '@app/core/model/entities/workflow/workflow';
import { WorkflowsService } from '@app/features/main/views/management/organization/workflows/workflows.service';
import CustomDuration from '@app/shared/extra/custom-duration';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AccessManager } from '@services/managers/access.manager';
import { AppManager } from '@services/managers/app.manager';
import { SnackbarManager } from '@services/managers/snackbar.manager';
import dayjs from 'dayjs';
import { BehaviorSubject, forkJoin, Observable } from 'rxjs';
import { finalize, map, shareReplay, switchMap, tap } from 'rxjs/operators';

@Component({
  selector: 'workflow-buttons',
  standalone: true,
  templateUrl: './workflow-buttons.component.html',
  styleUrls: ['./workflow-buttons.component.scss'],
  imports: [
    AsyncPipe,
    MatTooltip,
    MatIconButton,
    MatIcon,
    TranslatePipe,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WorkflowButtonsComponent implements OnChanges, OnInit {
  private readonly workflowService = inject(WorkflowsService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly snackbarManager = inject(SnackbarManager);
  private readonly translate = inject(TranslateService);
  private readonly appManager = inject(AppManager);
  private readonly appConfig = inject(AppConfig);
  protected readonly accessManager = inject(AccessManager);

  @Input({required: true})
  public entityType!: EntityTypeEnum;
  @Input({required: true})
  public view!: ManualWorkflowViewEnum;
  @Input()
  public selectedEntityIds?: string[] = [];
  private readonly reloadSubject = new BehaviorSubject<void>(undefined);
  protected readonly Permission = PermissionEnum;
  protected readonly loadingWorkflowIds = new Set<number>();
  protected readonly tokenLimit: number = this.appManager.currentOrganization.properties['workflowTokenLimit'] ?? Infinity;
  protected remainingTokens: number = 0;

  public readonly workflows$: Observable<Workflow[]> = this.reloadSubject.pipe(
    switchMap(() =>
      forkJoin([
        this.workflowService.loadAllWorkflows(EntityTypeEnum.ORGANIZATION, ActionTypeEnum.MANUAL),
        this.workflowService.loadAllWorkflows(this.entityType, ActionTypeEnum.MANUAL)
      ])
    ),
    map(([entityWorkflows, organizationWorkflows]) =>
      [...entityWorkflows, ...organizationWorkflows]
        .filter(workflow => workflow.config.btnParams?.views?.includes(this.view))
    ),
    shareReplay({bufferSize: 1, refCount: true}),
    takeUntilDestroyed(this.destroyRef)
  );

  /**
   * Load remaining workflow tokens.
   */
  public ngOnInit() {
    this.workflowService.remainingWorkflowTokens(this.appManager.currentOrganization.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(remainingTokens => this.remainingTokens = remainingTokens);
  }

  /**
   * Listen for changes on buttons parameters.
   * @param changes The changed properties.
   */
  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['entityType'] || changes['view'] || changes['selectedEntityIds']) {
      this.reloadSubject.next();
    }
  }

  /**
   * Execute a manual workflow from the current UI context.
   * @param workflow Workflow to execute.
   */
  public executeWorkflow(workflow: Workflow): void {
    const disabled = workflow.config?.btnParams?.requiresEntitySelection && this.selectedEntityIds.length === 0;
    if (disabled || this.loadingWorkflowIds.has(workflow.id)) {
      return;
    }

    this.loadingWorkflowIds.add(workflow.id);
    const entityIds: string[] = workflow.entityType == EntityTypeEnum.ORGANIZATION ?
      [this.appManager.currentOrganization.id] : this.selectedEntityIds;

    this.workflowService.executeManualWorkflow(workflow.id, entityIds)
      .pipe(
        switchMap(() => this.workflowService.remainingWorkflowTokens(this.appManager.currentOrganization.id)),
        tap(remainingTokens => this.remainingTokens = remainingTokens),
        finalize(() => {
          this.loadingWorkflowIds.delete(workflow.id);
          this.reloadSubject.next();
          this.snackbarManager.showActionSnackbar(this.translate.instant('SUCCESS.WORKFLOW_EXECUTED'));
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  /**
   * Check if workflow should be disabled.
   * @param workflow Workflow that is verified.
   * @return True if workflow should be disabled, false otherwise.
   * @protected
   */
  protected isWorkflowDisabled(workflow: Workflow): boolean {
    const requiresEntitySelection = workflow.config.btnParams?.requiresEntitySelection;
    return this.getWorkflowCooldown(workflow) > 0 || requiresEntitySelection && this.selectedEntityIds.length === 0;
  }

  /**
   * Get Workflow cooldown in seconds.
   * @param workflow Workflow that cooldown is requested for.
   * @return Workflow cooldown in seconds.
   */
  protected getWorkflowCooldown(workflow: Workflow): number {
    const remainingSeconds = workflow.lastExecutedAt
      .add(this.appConfig.WORKFLOW_COOLDOWN, 'minute')
      .diff(dayjs(), 'second');
    return Number.isNaN(remainingSeconds) ? 0 : Math.max(0, remainingSeconds);
  }

  /**
   * Get a human-readable cooldown string for display in the tooltip.
   * Shows minutes when >= 60 seconds remain, otherwise seconds.
   * @param workflow Workflow that cooldown is requested for.
   * @return Formatted remaining cooldown (e.g. "5min" or "45s").
   */
  protected getWorkflowCooldownDisplay(workflow: Workflow): string {
    return CustomDuration.humanize(dayjs.duration(this.getWorkflowCooldown(workflow), 'seconds'), ['m', 's']);
  }
}
